import React from "react";
import { Input, InputNumber, DatePicker, Switch, Select } from "@arco-design/web-react";

// The definitions visible for a record of the given type: non-archived fields that
// are either universal (no applies_to_type_ids) or scoped to this type.
export function visibleFields(definitions, typeId) {
  return (definitions || []).filter((d) => {
    if (d.archived) return false;
    const scope = d.applies_to_type_ids;
    if (!scope || scope.length === 0) return true; // universal
    return !!typeId && scope.indexOf(typeId) >= 0;
  });
}

// Per-type format validators (applied to non-empty visible values). Geographic
// name fields (city/state/country) allow accented letters + common punctuation.
const NAME_RE = /^[A-Za-zÀ-ſ\s.'-]+$/;
const FORMAT = {
  email: { ok: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), msg: "must be a valid email" },
  phone: { ok: (v) => /^[+\d\s().-]+$/.test(v) && (v.match(/\d/g) || []).length >= 7, msg: "must be a valid phone number" },
  zip: { ok: (v) => /^\d{5}(-\d{4})?$/.test(v), msg: "must be a 5-digit ZIP" },
  city: { ok: (v) => NAME_RE.test(v), msg: "should contain letters only" },
  state: { ok: (v) => NAME_RE.test(v), msg: "should contain letters only" },
  country: { ok: (v) => NAME_RE.test(v), msg: "should contain letters only" },
};
const PLACEHOLDER = { email: "name@example.com", phone: "(555) 123-4567", zip: "12345", country: "United States" };

// Returns "Label message" strings for visible fields whose non-empty value fails
// its type's format check.
export function formatErrors(definitions, values, typeId) {
  const out = [];
  visibleFields(definitions, typeId).forEach((d) => {
    const v = values ? values[d.key] : undefined;
    if (v === undefined || v === null || v === "") return;
    const f = FORMAT[d.field_type];
    if (f && !f.ok(String(v))) out.push(`${d.label} ${f.msg}`);
  });
  return out;
}

// Returns the labels of required, *visible* fields whose value is empty — so
// hidden (wrong-type) required fields never block a save.
export function missingRequiredFields(definitions, values, typeId) {
  return visibleFields(definitions, typeId)
    .filter((d) => d.required)
    .filter((d) => {
      const v = values ? values[d.key] : undefined;
      return v === undefined || v === null || v === "";
    })
    .map((d) => d.label);
}

// Renders definition-driven inputs for an entity's custom fields, scoped to the
// record's current type. `values` = the entity's custom_fields object;
// `onChange(nextValues)` gets the updated object.
export default function CustomFieldInputs({ definitions, values, typeId, onChange }) {
  const visible = visibleFields(definitions, typeId);
  if (!visible.length) return null;

  const set = (key, v) => onChange({ ...(values || {}), [key]: v });

  return (
    <div>
      {visible.map((d) => (
        <div key={d.id} style={{ marginTop: 8 }}>
          <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>
            {d.label}{d.required ? " *" : ""}
          </div>
          {renderInput(d, values ? values[d.key] : undefined, (nv) => set(d.key, nv))}
        </div>
      ))}
    </div>
  );
}

function renderInput(d, v, setV) {
  switch (d.field_type) {
    case "number":
      return <InputNumber style={{ width: "100%" }} value={v} onChange={setV} />;
    case "boolean":
      return <Switch checked={!!v} onChange={setV} />;
    case "date":
      return <DatePicker style={{ width: "100%" }} value={v || undefined} onChange={(ds) => setV(ds || null)} />;
    case "select":
      return (
        <Select
          allowClear
          style={{ width: "100%" }}
          value={v || undefined}
          onChange={(nv) => setV(nv || null)}
          options={(d.options || []).map((o) => ({ label: o, value: o }))}
        />
      );
    default:
      // text + the string-ish types (email/phone/zip/city/state/country).
      return <Input value={v || ""} placeholder={PLACEHOLDER[d.field_type]} onChange={setV} />;
  }
}
